import React from 'react';
import {
    alpha,
    Box,
    Chip,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import {
    ArrowForward,
    CheckCircle,
    ErrorOutline,
    Inventory2,
    Restaurant,
    Science,
    TableChart,
    WarningAmber,
} from '@mui/icons-material';

const workbookSheets = [
    'Products',
    'Formulations',
    'Ingredients',
    'Quantities',
];

const importRows = [
    {
        ingredient: 'Milk (Cow)',
        quantity: '82.6%',
        status: 'Mapped',
        tone: 'success',
    },
    {
        ingredient: 'Canada blueberry',
        quantity: '10.0%',
        status: 'Mapped',
        tone: 'success',
    },
    {
        ingredient: 'Whole milk powder',
        quantity: '7.0%',
        status: 'Mapped',
        tone: 'success',
    },
    {
        ingredient: 'Pectin',
        quantity: '0.4%',
        status: 'Review',
        tone: 'warning',
    },
];

function StatusPill({ status, tone = 'success' }) {
    const isWarning = tone === 'warning';

    return (
        <Box
            sx={{
                px: 0.65,
                py: 0.28,
                borderRadius: 999,
                fontSize: 9.3,
                fontWeight: 900,
                lineHeight: 1,
                whiteSpace: 'nowrap',
                color: isWarning ? 'warning.dark' : 'success.dark',
                bgcolor: (theme) =>
                    isWarning
                        ? alpha(theme.palette.warning.main, 0.14)
                        : alpha(theme.palette.success.main, 0.12),
                border: '1px solid',
                borderColor: (theme) =>
                    isWarning
                        ? alpha(theme.palette.warning.main, 0.3)
                        : alpha(theme.palette.success.main, 0.28),
            }}
        >
            {status}
        </Box>
    );
}

function WorkbookSheet({ label }) {
    return (
        <Stack
            direction="row"
            spacing={0.65}
            alignItems="center"
            sx={{
                px: 0.85,
                py: 0.65,
                borderRadius: 1.4,
                bgcolor: (theme) => alpha(theme.palette.grey[500], 0.055),
                minWidth: 0,
            }}
        >
            <TableChart sx={{ fontSize: 13, color: 'primary.main', flexShrink: 0 }} />

            <Typography
                variant="caption"
                sx={{
                    fontWeight: 800,
                    fontSize: 10.3,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {label}
            </Typography>
        </Stack>
    );
}

export default function BulkProductFormulationImporterVisual() {
    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                minHeight: 300,
                p: 1.6,
                bgcolor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
            }}
        >
            <Paper
                variant="outlined"
                sx={{
                    width: '100%',
                    maxWidth: '100%',
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    boxShadow: '0 14px 28px rgba(15, 23, 42, 0.05)',
                }}
            >
                {/* Header */}
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        px: 1.7,
                        py: 1.2,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        minWidth: 0,
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <Box
                            sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                                color: 'primary.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <TableChart sx={{ fontSize: 16 }} />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 800,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                Bulk product and formulation importer
                            </Typography>

                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    display: 'block',
                                    mt: 0.2,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                Import formulation spreadsheets with ingredients and quantities
                            </Typography>
                        </Box>
                    </Stack>

                    <Chip
                        label="XLSX import"
                        size="small"
                        sx={{
                            height: 23,
                            fontSize: 10.5,
                            fontWeight: 800,
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                            color: 'primary.main',
                            flexShrink: 0,
                            ml: 1,
                        }}
                    />
                </Stack>

                {/* Body */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'minmax(0, 0.82fr) minmax(0, 1.38fr)',
                        },
                        minHeight: 250,
                        overflow: 'hidden',
                    }}
                >
                    {/* Left: workbook */}
                    <Box
                        sx={{
                            p: 1.6,
                            borderRight: { sm: '1px solid rgba(15, 23, 42, 0.08)' },
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.016),
                            display: 'flex',
                            flexDirection: 'column',
                            minWidth: 0,
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                                mb: 1,
                                fontWeight: 800,
                                color: 'text.secondary',
                                textTransform: 'uppercase',
                                letterSpacing: 0.4,
                            }}
                        >
                            Source workbook
                        </Typography>

                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1.2,
                                borderRadius: 2,
                                bgcolor: 'background.paper',
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                minWidth: 0,
                            }}
                        >
                            <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 1.2, minWidth: 0 }}>
                                <Box
                                    sx={{
                                        width: 30,
                                        height: 30,
                                        borderRadius: '50%',
                                        bgcolor: (theme) => alpha(theme.palette.success.main, 0.1),
                                        color: 'success.main',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                    }}
                                >
                                    <TableChart sx={{ fontSize: 16 }} />
                                </Box>

                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'block',
                                            fontWeight: 900,
                                            fontSize: 10.8,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        blueberry_yogurt_formulation.xlsx
                                    </Typography>

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            display: 'block',
                                            fontSize: 9.8,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        Product metadata and recipe sheet
                                    </Typography>
                                </Box>
                            </Stack>

                            <Stack spacing={0.7} sx={{ flex: 1, justifyContent: 'space-evenly' }}>
                                {workbookSheets.map((sheet) => (
                                    <WorkbookSheet key={sheet} label={sheet} />
                                ))}
                            </Stack>
                        </Paper>
                    </Box>

                    {/* Right: import table + output */}
                    <Box
                        sx={{
                            p: 1.45,
                            display: 'grid',
                            gridTemplateRows: 'auto 1fr auto',
                            gap: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                        }}
                    >
                        {/* Flow */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
                                gap: 0.8,
                                alignItems: 'center',
                                minWidth: 0,
                            }}
                        >
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 1,
                                    borderRadius: 2,
                                    bgcolor: (theme) => alpha(theme.palette.grey[500], 0.025),
                                    minWidth: 0,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', fontWeight: 800, fontSize: 9.5 }}
                                >
                                    Input
                                </Typography>

                                <Typography
                                    sx={{
                                        mt: 0.25,
                                        fontSize: 13,
                                        lineHeight: 1.2,
                                        fontWeight: 900,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    XLSX workbook
                                </Typography>
                            </Paper>

                            <ArrowForward sx={{ fontSize: 17, color: 'primary.main', flexShrink: 0 }} />

                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 1,
                                    borderRadius: 2,
                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.035),
                                    borderColor: (theme) => alpha(theme.palette.primary.main, 0.18),
                                    minWidth: 0,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', fontWeight: 800, fontSize: 9.5 }}
                                >
                                    Output
                                </Typography>

                                <Typography
                                    sx={{
                                        mt: 0.25,
                                        fontSize: 13,
                                        lineHeight: 1.2,
                                        fontWeight: 900,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    Imported formulation
                                </Typography>
                            </Paper>
                        </Box>

                        {/* Import table */}
                        <Paper
                            variant="outlined"
                            sx={{
                                borderRadius: 2,
                                overflow: 'hidden',
                                bgcolor: 'background.paper',
                                minWidth: 0,
                            }}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{
                                    px: 1.1,
                                    py: 0.8,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    minWidth: 0,
                                }}
                            >
                                <Stack direction="row" spacing={0.65} alignItems="center" sx={{ minWidth: 0 }}>
                                    <Inventory2 sx={{ fontSize: 15, color: 'primary.main', flexShrink: 0 }} />

                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 900,
                                            color: 'text.secondary',
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.35,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        Imported ingredients and quantities
                                    </Typography>
                                </Stack>

                                <StatusPill status="1 review" tone="warning" />
                            </Stack>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'minmax(0, 1fr) 0.55fr auto',
                                    columnGap: 0.8,
                                    px: 1.05,
                                    py: 0.68,
                                    bgcolor: (theme) => alpha(theme.palette.grey[500], 0.04),
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                {['Ingredient', 'Qty', 'Status'].map((head) => (
                                    <Typography
                                        key={head}
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontWeight: 800, fontSize: 9.8 }}
                                    >
                                        {head}
                                    </Typography>
                                ))}
                            </Box>

                            {importRows.map((row, index) => (
                                <Box
                                    key={row.ingredient}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'minmax(0, 1fr) 0.55fr auto',
                                        columnGap: 0.8,
                                        alignItems: 'center',
                                        px: 1.05,
                                        py: 0.68,
                                        borderBottom:
                                            index !== importRows.length - 1 ? '1px solid' : 'none',
                                        borderColor: 'divider',
                                        bgcolor:
                                            row.tone === 'warning'
                                                ? (theme) => alpha(theme.palette.warning.main, 0.045)
                                                : 'background.paper',
                                    }}
                                >
                                    <Stack direction="row" spacing={0.55} alignItems="center" sx={{ minWidth: 0 }}>
                                        {row.tone === 'warning' ? (
                                            <WarningAmber sx={{ fontSize: 13, color: 'warning.main', flexShrink: 0 }} />
                                        ) : (
                                            <CheckCircle sx={{ fontSize: 13, color: 'success.main', flexShrink: 0 }} />
                                        )}

                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 900,
                                                fontSize: 10.2,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {row.ingredient}
                                        </Typography>
                                    </Stack>

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontWeight: 800, fontSize: 10.2 }}
                                    >
                                        {row.quantity}
                                    </Typography>

                                    <StatusPill status={row.status} tone={row.tone} />
                                </Box>
                            ))}
                        </Paper>

                        {/* Imported formulation */}
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1,
                                borderRadius: 2,
                                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.025),
                                borderColor: (theme) => alpha(theme.palette.primary.main, 0.16),
                                minWidth: 0,
                            }}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={1}
                                sx={{ minWidth: 0 }}
                            >
                                <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}>
                                    <Box
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: '50%',
                                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                            color: 'primary.main',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <Science sx={{ fontSize: 15 }} />
                                    </Box>

                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: 'block',
                                                fontWeight: 900,
                                                fontSize: 10.8,
                                                lineHeight: 1.2,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            Greek Style Yogurt · Blueberry prototype v3
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                display: 'block',
                                                fontSize: 9.8,
                                                fontWeight: 700,
                                                mt: 0.2,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            Formulation imported with 4 ingredients · total 100.0%
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Stack direction="row" spacing={0.55} alignItems="center" sx={{ flexShrink: 0 }}>
                                    <Restaurant sx={{ fontSize: 14, color: 'primary.main' }} />
                                    <ErrorOutline sx={{ fontSize: 14, color: 'warning.main' }} />
                                </Stack>
                            </Stack>
                        </Paper>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
}